import { http } from './request'

/** P1 证书认证结果 */
export interface JitP1AuthResult {
  success?: boolean
  errCode?: string
  errDesc?: string
  certDn?: string
  certSn?: string
  certAttributes?: Record<string, string>
  umsAttributes?: Record<string, string>
  pmsAttributes?: Record<string, string>
  customAttributes?: Record<string, string>
}

/** 服务端持久化的认证绑定信息 */
export interface JitAuthBindInfo {
  bound: boolean
  bindTime?: string
  certDn?: string
  certSn?: string
  umsAttributes?: Record<string, string>
  certAttributes?: Record<string, string>
  pmsAttributes?: Record<string, string>
  customAttributes?: Record<string, string>
}

export interface JitAuthBindPayload {
  certSn: string
  certDn?: string
  certAttributes?: Record<string, string>
  umsAttributes?: Record<string, string>
  pmsAttributes?: Record<string, string>
  customAttributes?: Record<string, string>
}

export interface JitCertLoginPayload {
  random: string
  signedData: string
  certBase64: string
  hashAlg: string
  /** 控件选中的证书序列号，网关不回传 SN 时后端靠这个兜底 */
  certSn: string
}

/**
 * 对应 authen.js: random()
 * 厂家脚本在 ajax error 时返回空字符串，不抛异常。
 */
export async function fetchJitAuthRandom(): Promise<string> {
  try {
    return await http.post<string>('/auth/b/jitGWRandom', {}, { skipErrorMessage: true })
  } catch {
    return ''
  }
}

/**
 * 对应 authen.js: P1certAuth(random, signed_data, certBase64, hashAlg)
 * 厂家脚本在 ajax error 时返回空字符串；成功时返回认证结果对象。
 */
export async function submitJitP1Auth(
  random: string,
  signedData: string | undefined,
  certBase64: string | undefined,
  hashAlg: string | undefined,
): Promise<JitP1AuthResult | ''> {
  try {
    return await http.post<JitP1AuthResult>(
      '/auth/b/jitGWAuthP1',
      {
        random,
        signedData: signedData ?? '',
        certBase64: certBase64 ?? '',
        hashAlg: hashAlg ?? '',
      },
      { skipErrorMessage: true },
    )
  } catch {
    return ''
  }
}

/** 登录页-获取 JIT 认证随机数 */
export async function fetchJitLoginRandom(): Promise<string> {
  try {
    return await http.post<string>('/auth/b/jitLoginRandom', {}, { skipErrorMessage: true })
  } catch {
    return ''
  }
}

/** 登录页-JIT 证书认证并登录，成功返回 token */
export async function loginByJitCert(payload: JitCertLoginPayload): Promise<string> {
  const certSn = String(payload.certSn || '').trim()
  if (!certSn) {
    throw new Error('未获取到证书序列号，请重新选择证书后再登录')
  }
  return await http.post<string>(
    '/auth/b/doLoginByJitCert',
    {
      random: payload.random,
      signedData: payload.signedData,
      certBase64: payload.certBase64,
      hashAlg: payload.hashAlg,
      certSn,
    },
    { skipErrorMessage: true },
  )
}

/** 查询当前登录用户的 JIT 认证绑定信息 */
export async function fetchJitAuthBindInfo(): Promise<JitAuthBindInfo> {
  return await http.get<JitAuthBindInfo>('/auth/b/jitGWBindInfo')
}

/** 将认证结果绑定到当前登录账号 */
export async function bindJitAuthToAccount(payload: JitAuthBindPayload): Promise<void> {
  await http.post('/auth/b/jitGWBind', payload)
}

function formatBindTime(value?: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('zh-CN', { hour12: false })
}

export function formatJitAuthBindTime(bindTime?: string): string {
  return formatBindTime(bindTime)
}

/** 网关属性名不统一时，从认证结果里取序列号 */
export function pickJitCertSn(result?: JitP1AuthResult | null, fallback?: string): string {
  const direct = String(result?.certSn || '').trim()
  if (direct) return direct
  const attrs: Record<string, string> = {
    ...(result?.certAttributes || {}),
    ...(result?.umsAttributes || {}),
    ...(result?.customAttributes || {}),
  }
  const aliases = [
    'X509Certificate.SerialNumber',
    'SerialNumber',
    'serialNumber',
    'certSN',
    'certSn',
    'X509SerialNumber',
    'serial',
    'SN',
  ]
  for (const key of aliases) {
    const value = String(attrs[key] || '').trim()
    if (value) return value
  }
  for (const [key, value] of Object.entries(attrs)) {
    if (value && /serial/i.test(key)) return String(value).trim()
  }
  return String(fallback || '').trim()
}
